/**
 * Checklists Settings Page (Phase 9.6)
 *
 * Manage organization checklists - recurring verification tasks.
 * Features search, station/frequency filters, and modal form for create/edit with item management.
 */

import { useState, useEffect } from 'react';
import {
  ClipboardList,
  Plus,
  Search,
  AlertCircle,
  Loader2,
  Pencil,
  Trash2,
  X,
  Locate,
  ChevronDown,
  PlusCircle,
  Clock,
} from 'lucide-react';
import {
  useChecklists,
  useChecklist,
  useDeleteChecklist,
  useCreateChecklist,
  useUpdateChecklist,
  useAddChecklistItem,
} from '../../hooks/useChecklists';
import { useStationLookup } from '../../hooks/useStations';
import { useCurrentUser } from '../../hooks/useUsers';
import type { ChecklistWithStation, ChecklistFrequency } from '@guild/shared';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

const frequencyLabels: Record<ChecklistFrequency, string> = {
  per_shift: 'Per Shift',
  daily: 'Daily',
  weekly: 'Weekly',
};

const frequencyColors: Record<ChecklistFrequency, { bg: string; text: string }> = {
  per_shift: { bg: 'bg-blue-50', text: 'text-blue-700' },
  daily: { bg: 'bg-purple-50', text: 'text-purple-700' },
  weekly: { bg: 'bg-amber-50', text: 'text-amber-700' },
};

export function ChecklistsPage() {
  useDocumentTitle('Checklists');
  // Phase 19.S frontend migration — read role from DB-canonical /api/me, not the JWT claim.
  const { data: currentUser } = useCurrentUser();
  const userRole = currentUser?.role;
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;

  const [searchQuery, setSearchQuery] = useState('');
  const [stationFilter, setStationFilter] = useState('');
  const [frequencyFilter, setFrequencyFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState<ChecklistWithStation | null>(null);

  const { data: checklists, isLoading, error } = useChecklists({
    search: searchQuery || undefined,
    stationId: stationFilter || undefined,
    frequency: frequencyFilter as ChecklistFrequency || undefined,
    includeInactive: showInactive,
  });

  const { data: stations } = useStationLookup();
  const deleteMutation = useDeleteChecklist();

  const handleCreate = () => {
    setEditingChecklist(null);
    setIsFormOpen(true);
  };

  const handleEdit = (checklist: ChecklistWithStation) => {
    setEditingChecklist(checklist);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this checklist?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingChecklist(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50">
            <ClipboardList size={24} className="text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Checklists
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Define recurring verification tasks for stations
            </p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={handleCreate}
            className="
              inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            Add Checklist
          </button>
        )}
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[280px]">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search checklists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="
              w-full rounded-xl border border-gray-200 bg-white
              pl-11 pr-4 py-2.5 text-sm
              placeholder:text-gray-400
              focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              transition-all duration-200
            "
          />
        </div>

        {/* Station filter */}
        <div className="relative">
          <Locate
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <select
            value={stationFilter}
            onChange={(e) => setStationFilter(e.target.value)}
            className="
              appearance-none rounded-xl border border-gray-200 bg-white
              py-2.5 pl-9 pr-10
              text-sm text-gray-700
              transition-colors duration-200
              hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
            "
          >
            <option value="">All Stations</option>
            {stations?.map((station) => (
              <option key={station.id} value={station.id}>
                {station.name}
              </option>
            ))}
          </select>
          <ChevronDown
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        </div>

        {/* Frequency filter */}
        <div className="relative">
          <Clock
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <select
            value={frequencyFilter}
            onChange={(e) => setFrequencyFilter(e.target.value)}
            className="
              appearance-none rounded-xl border border-gray-200 bg-white
              py-2.5 pl-9 pr-10
              text-sm text-gray-700
              transition-colors duration-200
              hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
            "
          >
            <option value="">All Frequencies</option>
            <option value="per_shift">Per Shift</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
          <ChevronDown
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        </div>

        {/* Inactive filter */}
        {isAdmin && (
          <label className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 bg-white text-guild-green focus:ring-guild-green focus:ring-offset-0"
            />
            <span className="text-sm font-medium text-gray-600">
              Show inactive
            </span>
          </label>
        )}
      </div>

      {/* Content */}
      {error ? (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Failed to load checklists
            </p>
            <p className="text-sm text-red-600">
              Please try refreshing the page or contact support if the issue persists.
            </p>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 size={32} className="text-guild-green animate-spin" />
          <p className="mt-4 text-sm text-gray-500">Loading checklists...</p>
        </div>
      ) : checklists && checklists.length > 0 ? (
        <ChecklistList
          checklists={checklists}
          isAdmin={isAdmin}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
        />
      ) : (
        <EmptyState
          searchQuery={searchQuery}
          isAdmin={isAdmin}
          onCreateClick={handleCreate}
        />
      )}

      {/* Form Dialog */}
      {isFormOpen && (
        <ChecklistForm
          checklist={editingChecklist}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}

// ============================================
// CHECKLIST LIST COMPONENT
// ============================================

interface ChecklistListProps {
  checklists: ChecklistWithStation[];
  isAdmin: boolean;
  onEdit: (checklist: ChecklistWithStation) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function ChecklistList({ checklists, isAdmin, onEdit, onDelete, isDeleting }: ChecklistListProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Checklist
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Station
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Frequency
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Items
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Status
            </th>
            {isAdmin && (
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {checklists.map((checklist) => {
            const freqStyle = frequencyColors[checklist.frequency];
            return (
              <tr
                key={checklist.id}
                className={`
                  group transition-colors
                  ${checklist.isActive ? 'hover:bg-gray-50/50' : 'bg-gray-50/30'}
                `}
              >
                {/* Name */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div
                      className={`
                        flex h-9 w-9 items-center justify-center rounded-lg
                        ${checklist.isActive ? 'bg-teal-50' : 'bg-gray-100'}
                      `}
                    >
                      <ClipboardList
                        size={16}
                        className={checklist.isActive ? 'text-teal-600' : 'text-gray-400'}
                      />
                    </div>
                    <div>
                      <span
                        className={`
                          text-sm font-semibold
                          ${checklist.isActive ? 'text-gray-900' : 'text-gray-400'}
                        `}
                      >
                        {checklist.name}
                      </span>
                      {checklist.description && (
                        <p className="text-xs text-gray-500 line-clamp-1 max-w-[200px]">
                          {checklist.description}
                        </p>
                      )}
                    </div>
                  </div>
                </td>

                {/* Station */}
                <td className="px-6 py-4 whitespace-nowrap">
                  {checklist.stationName ? (
                    <div className="flex items-center gap-1.5">
                      <Locate
                        size={14}
                        className={checklist.isActive ? 'text-gray-400' : 'text-gray-300'}
                      />
                      <span
                        className={`
                          text-sm
                          ${checklist.isActive ? 'text-gray-600' : 'text-gray-400'}
                        `}
                      >
                        {checklist.stationName}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-300 italic text-sm">Not assigned</span>
                  )}
                </td>

                {/* Frequency */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`
                      inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold
                      ${checklist.isActive ? `${freqStyle.bg} ${freqStyle.text}` : 'bg-gray-100 text-gray-500'}
                    `}
                  >
                    <Clock size={12} />
                    {frequencyLabels[checklist.frequency]}
                  </span>
                </td>

                {/* Item Count */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`
                      text-sm font-medium
                      ${checklist.isActive ? 'text-gray-600' : 'text-gray-400'}
                    `}
                  >
                    {checklist.itemCount} {checklist.itemCount === 1 ? 'item' : 'items'}
                  </span>
                </td>

                {/* Status */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`
                      inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold
                      ${checklist.isActive
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-gray-100 text-gray-500'
                      }
                    `}
                  >
                    <span
                      className={`
                        h-1.5 w-1.5 rounded-full
                        ${checklist.isActive ? 'bg-emerald-500' : 'bg-gray-400'}
                      `}
                    />
                    {checklist.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>

                {/* Actions */}
                {isAdmin && (
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onEdit(checklist)}
                        className="
                          inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                          text-sm font-medium text-gray-600
                          hover:bg-gray-100 hover:text-gray-900
                          transition-colors
                        "
                      >
                        <Pencil size={14} />
                        Edit
                      </button>
                      {checklist.isActive && (
                        <button
                          onClick={() => onDelete(checklist.id)}
                          disabled={isDeleting}
                          className="
                            inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                            text-sm font-medium text-red-600
                            hover:bg-red-50 hover:text-red-700
                            disabled:opacity-50 disabled:cursor-not-allowed
                            transition-colors
                          "
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// EMPTY STATE COMPONENT
// ============================================

interface EmptyStateProps {
  searchQuery: string;
  isAdmin: boolean;
  onCreateClick: () => void;
}

function EmptyState({ searchQuery, isAdmin, onCreateClick }: EmptyStateProps) {
  return (
    <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 p-12 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-teal-50">
        <ClipboardList size={28} className="text-teal-600" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-gray-900">
        {searchQuery ? 'No matching checklists' : 'No checklists yet'}
      </h3>
      <p className="mt-1.5 text-sm text-gray-500 max-w-sm mx-auto">
        {searchQuery
          ? 'Try adjusting your search or filters to find what you\'re looking for.'
          : 'Create your first checklist to start tracking recurring verification tasks.'}
      </p>
      {isAdmin && !searchQuery && (
        <button
          onClick={onCreateClick}
          className="
            mt-6 inline-flex items-center gap-2 rounded-xl
            bg-guild-green px-5 py-2.5
            text-sm font-semibold text-white
            hover:bg-guild-green/95
            transition-colors
          "
        >
          <Plus size={18} strokeWidth={2.5} />
          Create Checklist
        </button>
      )}
    </div>
  );
}

// ============================================
// CHECKLIST FORM COMPONENT
// ============================================

interface ChecklistFormProps {
  checklist: ChecklistWithStation | null;
  onClose: () => void;
}

interface FormItem {
  id: string;
  description: string;
  isNew?: boolean;
}

function ChecklistForm({ checklist, onClose }: ChecklistFormProps) {
  const isEdit = !!checklist;
  const [name, setName] = useState(checklist?.name ?? '');
  const [stationId, setStationId] = useState(checklist?.stationId ?? '');
  const [description, setDescription] = useState(checklist?.description ?? '');
  const [frequency, setFrequency] = useState<ChecklistFrequency>(checklist?.frequency ?? 'daily');
  const [items, setItems] = useState<FormItem[]>([]);
  const [newItemText, setNewItemText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: stations } = useStationLookup();
  const { data: checklistDetail, isLoading: loadingItems } = useChecklist(isEdit ? checklist?.id ?? null : null);
  const createMutation = useCreateChecklist();
  const updateMutation = useUpdateChecklist();
  const addItemMutation = useAddChecklistItem();

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Populate items from fetched checklist detail
  useEffect(() => {
    if (checklistDetail?.items) {
      setItems(checklistDetail.items.map((item: { id: string; description: string }) => ({
        id: item.id,
        description: item.description,
        isNew: false,
      })));
    }
  }, [checklistDetail]);

  const handleAddItem = () => {
    if (!newItemText.trim()) return;

    setItems([...items, {
      id: `new-${Date.now()}`,
      description: newItemText.trim(),
      isNew: true,
    }]);
    setNewItemText('');
  };

  const handleRemoveItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleUpdateItem = (id: string, description: string) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, description } : item
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (items.length === 0) {
      setError('At least one checklist item is required');
      return;
    }

    try {
      if (isEdit && checklist) {
        // Update checklist metadata
        await updateMutation.mutateAsync({
          id: checklist.id,
          data: {
            name: name.trim(),
            stationId: stationId || null,
            description: description.trim() || null,
            frequency,
          },
        });

        // Handle item changes (add new, delete removed)
        // For simplicity, we only support adding new items in edit mode for now
        const newItems = items.filter(item => item.isNew);
        for (const item of newItems) {
          await addItemMutation.mutateAsync({
            checklistId: checklist.id,
            data: { description: item.description },
          });
        }

        onClose();
      } else {
        // Create new checklist with items
        const itemsData = items.map(item => ({ description: item.description }));
        await createMutation.mutateAsync({
          name: name.trim(),
          stationId: stationId || undefined,
          description: description.trim() || undefined,
          frequency,
          items: itemsData,
        });
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 max-h-[90vh] overflow-auto rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4 z-10">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Checklist' : 'Create Checklist'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Checklist Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Shift Cleaning Checklist"
              className="
                w-full rounded-xl border border-gray-200 bg-white
                px-4 py-2.5 text-sm
                placeholder:text-gray-400
                focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                transition-all
              "
            />
          </div>

          {/* Station */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Station
            </label>
            <div className="relative">
              <select
                value={stationId}
                onChange={(e) => setStationId(e.target.value)}
                className="
                  w-full appearance-none rounded-xl border border-gray-200 bg-white
                  px-4 py-2.5 pr-10 text-sm
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  transition-all
                "
              >
                <option value="">No station (org-wide)</option>
                {stations?.map((station) => (
                  <option key={station.id} value={station.id}>
                    {station.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>

          {/* Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Frequency <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as ChecklistFrequency)}
                className="
                  w-full appearance-none rounded-xl border border-gray-200 bg-white
                  px-4 py-2.5 pr-10 text-sm
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  transition-all
                "
              >
                <option value="per_shift">Per Shift</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
              <ChevronDown
                size={16}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="
                w-full rounded-xl border border-gray-200 bg-white
                px-4 py-2.5 text-sm
                placeholder:text-gray-400
                focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                transition-all resize-none
              "
            />
          </div>

          {/* Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Checklist Items <span className="text-red-500">*</span>
            </label>

            {loadingItems ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={20} className="text-gray-400 animate-spin" />
              </div>
            ) : (
              <>
                {/* Existing items */}
                <div className="space-y-2 mb-3">
                  {items.map((item, index) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-2 group"
                    >
                      <span className="text-xs text-gray-400 w-5">{index + 1}.</span>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleUpdateItem(item.id, e.target.value)}
                        className="
                          flex-1 rounded-lg border border-gray-200 bg-white
                          px-3 py-2 text-sm
                          focus:border-guild-green focus:outline-none focus:ring-1 focus:ring-guild-green/20
                          transition-all
                        "
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(item.id)}
                        className="
                          p-1.5 rounded-lg text-gray-400
                          hover:bg-red-50 hover:text-red-500
                          opacity-0 group-hover:opacity-100
                          transition-all
                        "
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new item */}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    placeholder="Add a checklist item..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddItem();
                      }
                    }}
                    className="
                      flex-1 rounded-lg border border-dashed border-gray-300 bg-gray-50
                      px-3 py-2 text-sm
                      placeholder:text-gray-400
                      focus:border-guild-green focus:outline-none focus:ring-1 focus:ring-guild-green/20 focus:bg-white
                      transition-all
                    "
                  />
                  <button
                    type="button"
                    onClick={handleAddItem}
                    disabled={!newItemText.trim()}
                    className="
                      inline-flex items-center gap-1 rounded-lg px-3 py-2
                      text-sm font-medium text-guild-green
                      hover:bg-guild-green/10
                      disabled:opacity-50 disabled:cursor-not-allowed
                      transition-colors
                    "
                  >
                    <PlusCircle size={16} />
                    Add
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="
                rounded-xl border border-gray-200 px-5 py-2.5
                text-sm font-medium text-gray-600
                hover:bg-gray-50 hover:border-gray-300
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || loadingItems}
              className="
                inline-flex items-center gap-2 rounded-xl
                bg-guild-green px-5 py-2.5
                text-sm font-semibold text-white
                hover:bg-guild-green/95
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              {isPending && <Loader2 size={16} className="animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Checklist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
